/**
 * useAxiomOrderFlow.tsx
 *
 * Shared Axiom peptide order + payment orchestration, so both the per-patient
 * Peptides tab and the account-wide Peptides page drive the same modals and
 * handlers (order/re-order, finish payment, cancel).
 *
 * Patient context is per-action: the per-patient tab passes its fixed patientId,
 * the global page passes each order's patientId (per-patient rows don't carry
 * one). The hook tracks the acting patient for the modals it renders.
 */
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useState } from 'react';
import { submitDelayPhrase } from '@/lib/peptideOrder';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getEnabledIntegrations,
  getIntegrationOnboarding,
  markIntegrationOrderPaid,
  cancelIntegrationOrder,
  retryIntegrationOrder,
  type CreateIntegrationOrderResult,
  type IntegrationOrder,
  type EnabledIntegration,
} from '@/services/integrations.service';
import { ChargePaymentModal, type ChargePaymentPrefillItem } from '@/pages/payments/ChargePaymentModal';
import { AxiomOrderModal, type AxiomCreatedOrder, type PaidReference } from './AxiomOrderModal';
import { AXIOM_VIALS_KEY, isShippingProduct, SHIPPING_LABEL } from './integrationAddOns';
import { RmmPrescriptionModal } from './RmmPrescriptionModal';
import { PaymentSourceModal } from './PaymentSourceModal';
import { useToast } from '@/hooks/useToast';
import { getErrorMessage } from '@/services/apiClient';

interface PayState {
  patientId: number;
  orderId: number;
  prefillItems: ChargePaymentPrefillItem[];
  /**
   * Combined cart: the OTHER orders the same payment pays for (the vials half
   * of a pens + vials checkout). Each is marked paid with the same
   * collectedPaymentID — the server accepts that because they share a
   * metadata.cartGroupId.
   */
  siblingOrderIds?: number[];
}

export interface AxiomOrderFlow {
  /** The enabled Axiom integration, or undefined when not enabled for the account. */
  axiom: EnabledIntegration | undefined;
  /**
   * True when this flow opens the COMBINED pens + vials cart for new orders —
   * asked for with `combineVials` and both modules enabled for the account.
   */
  combined: boolean;
  /** Whether the clinic has completed Axiom Connect onboarding. Ordering is
   *  blocked (button disabled, actions no-op with a toast) until this is true. */
  onboarded: boolean;
  /**
   * Whether `onboarded` is an answer yet. False while the onboarding status is
   * still loading, when `onboarded` reads false for EVERY practice — so a caller
   * acting on arrival (App Orders' Fulfill) must wait for this, or it shows an
   * onboarded practice "ordering is locked until onboarding" (SUP-000744 #4).
   */
  onboardingKnown: boolean;
  /**
   * Open the order modal for a fresh order for this patient.
   *
   * `paid` attaches a read-only "paid for" reference (the Fulfill flow from App
   * Orders). It is shown, never applied — see AxiomOrderModal.paidReference.
   */
  startNewOrder: (patientId: number, paid?: PaidReference | null) => void;
  /** Open the order modal prefilled from a prior order as a NEW order (Re-order). */
  reorder: (order: IntegrationOrder, patientId: number) => void;
  /**
   * Open the order modal to amend an order IN PLACE (SCRUM-1814) — items,
   * prescriber, directions and, the reason it was asked for, the shipping
   * address, while the order is still held awaiting payment.
   *
   * Only offered before the order reaches the pharmacy. Past that point nothing
   * changed here is sent to them, so an edit would leave our record disagreeing
   * with the prescription being filled; the server refuses it either way.
   */
  edit: (order: IntegrationOrder, patientId: number) => void;
  /**
   * Open the order modal to place this order AGAIN, AS IT WAS — same items and
   * the same shipping address it was sent to.
   *
   * Distinct from `reorder`, which is the refill: that one carries the
   * prescription and lets the ship-to come from the patient's own addresses,
   * because a months-old delivery address should not ride silently into a new
   * order. Cloning is the opposite statement — "this order, again" — and being
   * an explicit, separately named action is what makes carrying the address
   * safe rather than silent.
   */
  clone: (order: IntegrationOrder, patientId: number) => void;
  /**
   * Re-send a CANCELLED order: a clone (new order, same items and address)
   * that also records which cancelled order it replaces.
   */
  resend: (order: IntegrationOrder, patientId: number) => void;
  /** Open the payment-source chooser for a held order's fee (charge new vs
   *  link an existing payment). */
  finishPayment: (order: IntegrationOrder, patientId: number) => void;
  /** Cancel an order (server re-checks cancellability). */
  cancel: (order: IntegrationOrder, patientId: number) => Promise<void>;
  /**
   * Re-send a failed order to the pharmacy as ITSELF — same items, same charge,
   * no new order row. Distinct from `reorder`, which opens the modal to place a
   * new order (typically at a new dosage).
   */
  retry: (order: IntegrationOrder, patientId: number) => Promise<void>;
  /** The modals to render once at the page root. */
  modals: React.ReactNode;
}

/**
 * ONE "Shipping" line for a cart paid with one charge (SCRUM-2023).
 *
 * A mixed pens + vials cart is two Axiom orders, and each pays its own
 * shipping — the pens order Axiom's flat per-order fee, the vials order its
 * SHIPPING fee line — so the cart's charge used to list "Shipping" twice. The
 * patient sees one shipping fee: every line labelled SHIPPING_LABEL is folded
 * into a single line carrying the summed amount, where the first one was. The
 * total is unchanged. Lines that are not shipping keep their order.
 */
export function mergeShippingLines(items: ChargePaymentPrefillItem[]): ChargePaymentPrefillItem[] {
  const isShip = (l: ChargePaymentPrefillItem) => l.productTypeName === SHIPPING_LABEL;
  const ship = items.filter(isShip);
  if (ship.length < 2) return items;
  const amount = Math.round(
    ship.reduce((sum, l) => sum + (l.unitPrice ?? 0) * (l.quantity ?? 1), 0) * 100,
  ) / 100;
  const merged: ChargePaymentPrefillItem = {
    quoteItemCostId: ship[0].quoteItemCostId,
    quantity: 1,
    unitPrice: amount,
    productTypeName: SHIPPING_LABEL,
  };
  const out: ChargePaymentPrefillItem[] = [];
  let placed = false;
  for (const l of items) {
    if (!isShip(l)) { out.push(l); continue; }
    if (!placed) { out.push(merged); placed = true; }
  }
  return out;
}

export function useAxiomOrderFlow(opts: {
  onRefresh: () => void;
  /** Axiom-family integration key to order against ('axiom' pens — default —
   *  or 'axiom_vials'). Threads through the modal and every order/cancel call. */
  integrationKey?: string;
  /**
   * Pens flow only: when the account also has Axiom Vials, open NEW orders as
   * one combined cart (pens + vials + supplies) that is split into a pens order
   * and a vials order on submit and paid for once.
   *
   * Only new orders. Edit / re-order / clone / re-send act on an existing
   * order of ONE module, so they open the single-integration form for this
   * flow's key — callers keep a separate vials flow for vials rows' actions.
   */
  combineVials?: boolean;
}): AxiomOrderFlow {
  const { onRefresh, integrationKey = 'axiom', combineVials = false } = opts;
  /**
   * Partell runs through this same flow — payment, cancel, retry, re-order,
   * clone, re-send, edit — but a prescription is WRITTEN, not picked from a
   * peptide catalogue, so its orders open the Partell prescription form
   * instead of AxiomOrderModal, and it has no Axiom onboarding to gate on.
   */
  const isRmm = integrationKey === 'rmm';
  const toast = useToast();
  const queryClient = useQueryClient();

  const { data: integrations = [] } = useQuery({
    queryKey: ['integrations', 'enabled'],
    queryFn: getEnabledIntegrations,
    staleTime: 5 * 60_000,
  });
  const axiom = integrations.find((i) => i.key === integrationKey);
  // Vials ride on the pens onboarding (onboardingModuleId → pens server-side),
  // so the pens onboarding check below covers the combined cart too.
  const vialsIntegration = combineVials && integrationKey === 'axiom' && axiom
    ? integrations.find((i) => i.key === AXIOM_VIALS_KEY)
    : undefined;
  const combined = !!vialsIntegration;

  // Same query key as the admin page, so completing enrollment there unlocks
  // ordering here without a reload. Keyed per integration so pens/vials don't
  // share a cache entry.
  const { data: onboarding } = useQuery({
    queryKey: ['integration-onboarding', integrationKey],
    queryFn: () => getIntegrationOnboarding(integrationKey),
    enabled: !!axiom,
    staleTime: 5 * 60_000,
  });
  const onboarded = onboarding?.onboarded === true;
  const onboardingKnown = onboarding !== undefined;

  const [orderOpen, setOrderOpen] = useState(false);
  const [reorderSeed, setReorderSeed] = useState<IntegrationOrder | null>(null);
  /**
   * The order being edited in place. Distinct from `reorderSeed`: both prefill
   * the same form, but only this one makes the modal submit a PATCH instead of
   * creating a second order (AxiomOrderModal.editOrder).
   */
  const [editOrder, setEditOrder] = useState<IntegrationOrder | null>(null);
  /**
   * The order whose shipping address a clone should reuse. Separate from
   * `editOrder` because a clone creates a new order rather than amending one —
   * only the ship-to is inherited.
   */
  const [cloneShipFrom, setCloneShipFrom] = useState<IntegrationOrder | null>(null);
  /** The cancelled order a Re-send replaces; null for every other kind of order. */
  const [resendFrom, setResendFrom] = useState<number | null>(null);
  /** The order waiting on the cancel confirmation dialog. */
  const [pendingCancel, setPendingCancel] = useState<{ order: IntegrationOrder; patientId: number } | null>(null);
  const [paidReference, setPaidReference] = useState<PaidReference | null>(null);
  const [activePatientId, setActivePatientId] = useState<number>(0);
  const [pay, setPay] = useState<PayState | null>(null);
  // Payment-source chooser step: holds the would-be pay payload while staff
  // picks "charge now" (→ setPay) vs "link an existing payment" (→ mark-paid).
  const [paySource, setPaySource] = useState<PayState | null>(null);

  const refresh = () => {
    onRefresh();
    // Also refresh the per-patient cache so a patient tab open elsewhere updates.
    // Keyed per integration ('patient-axiom-orders' / 'patient-axiom_vials-orders')
    // to match IntegrationOrdersSubTab's query key.
    void queryClient.invalidateQueries({ queryKey: [`patient-${integrationKey}-orders`] });
    // A combined cart also places a vials order.
    if (combined) void queryClient.invalidateQueries({ queryKey: [`patient-${AXIOM_VIALS_KEY}-orders`] });
  };

  function requireOnboarding(): boolean {
    if (isRmm || onboarded) return true;
    toast.error('Peptide ordering is locked until your practice completes Axiom onboarding in Admin → Integrations → Axiom.');
    return false;
  }

  function startNewOrder(patientId: number, paid: PaidReference | null = null) {
    if (!requireOnboarding()) return;
    setResendFrom(null);
    setActivePatientId(patientId);
    setReorderSeed(null);
    setPaidReference(paid);
    setOrderOpen(true);
  }

  function reorder(order: IntegrationOrder, patientId: number) {
    if (!requireOnboarding()) return;
    setResendFrom(null);
    setActivePatientId(patientId);
    setReorderSeed(order);
    setCloneShipFrom(null);
    // A re-order carries its own items; a stale paid reference from an earlier
    // Fulfill would sit above them describing a different payment.
    setPaidReference(null);
    setOrderOpen(true);
  }

  function edit(order: IntegrationOrder, patientId: number) {
    if (!requireOnboarding()) return;
    setActivePatientId(patientId);
    setEditOrder(order);
    // An edit is not a re-order and not a fulfilment: clear both, or the form
    // would prefill from a different order and show a payment that is not this
    // order's.
    setReorderSeed(null);
    setPaidReference(null);
    setOrderOpen(true);
  }

  function clone(order: IntegrationOrder, patientId: number) {
    if (!requireOnboarding()) return;
    setResendFrom(null);
    setActivePatientId(patientId);
    // Seeds the items as a NEW order (never a PATCH — editOrder stays null),
    // and the address explicitly, which is the whole difference from reorder.
    setReorderSeed(order);
    setCloneShipFrom(order);
    setEditOrder(null);
    setPaidReference(null);
    setOrderOpen(true);
  }

  function resend(order: IntegrationOrder, patientId: number) {
    if (!requireOnboarding()) return;
    clone(order, patientId);
    setResendFrom(order.integrationOrderId);
  }

  function handleOrdered(result: CreateIntegrationOrderResult, prefillItems: ChargePaymentPrefillItem[]) {
    handleOrderedGroup([{ key: integrationKey, result, prefillItems }]);
  }

  /**
   * Every order a submit created — one, or two for a combined pens + vials
   * cart. The patient pays ONCE for all of them.
   *
   * Either way the payment-source chooser comes first: the patient may have
   * already paid on the companion site (App Orders → Fulfill opens exactly
   * this). For a split cart the chooser carries the sibling orders, so an
   * existing payment is linked to EVERY order of the cart, and "Charge Payment
   * now" takes one charge for the combined total, released to each order —
   * the server accepts both because the orders share a cartGroupId.
   */
  function handleOrderedGroup(orders: AxiomCreatedOrder[]) {
    refresh();
    setReorderSeed(null);
    setResendFrom(null);
    const toCharge = orders.filter((o) => o.result.billingMode === 'charge_patient');
    if (toCharge.length === 0) return;
    if (toCharge.length === 1) {
      const [only] = toCharge;
      setPaySource({ patientId: activePatientId, orderId: only.result.integrationOrderId, prefillItems: only.prefillItems });
      return;
    }
    setPaySource({
      patientId: activePatientId,
      orderId: toCharge[0].result.integrationOrderId,
      siblingOrderIds: toCharge.slice(1).map((o) => o.result.integrationOrderId),
      prefillItems: mergeShippingLines(toCharge.flatMap((o) => o.prefillItems)),
    });
  }

  function finishPayment(order: IntegrationOrder, patientId: number) {
    const meta = order.metadata ?? {};
    const lineItems = (meta.lineItems ?? []) as Array<{
      integrationProductId?: number; name?: string; quantity?: number; unitPrice?: number;
    }>;

    const prefillItems: ChargePaymentPrefillItem[] = [];
    for (const li of lineItems) {
      const product = axiom?.products.find((p) => p.integrationProductId === li.integrationProductId);
      if (product?.quoteItemCostId == null) continue;
      prefillItems.push({
        quoteItemCostId: product.quoteItemCostId,
        quantity: li.quantity ?? 1,
        unitPrice: li.unitPrice ?? product.price,
        // A vials order's SHIPPING fee line reads "Shipping", never its
        // catalog name ("Cold"/"Standard" — the pharmacy decides).
        productTypeName: isShippingProduct(product) ? SHIPPING_LABEL : (li.name ?? product.name),
      });
    }

    const shipping = (meta.shipping ?? {}) as { price?: number };
    const shippingFee = shipping.price ?? 0;
    if (shippingFee > 0 && prefillItems.length > 0) {
      prefillItems.push({
        quoteItemCostId: prefillItems[0].quoteItemCostId,
        quantity: 1,
        unitPrice: shippingFee,
        productTypeName: SHIPPING_LABEL,
      });
    }

    // An integration whose order carries no `metadata.lineItems` — Partell
    // keeps its prescriptions in integration_order_items instead, because a
    // script is not a catalog line. Its charge is a single flat fee, so it is
    // rebuilt as one prefill line against the integration's billing
    // placeholder product (still where the payment's quoteItemCostId comes
    // from — Partell has no per-drug billing row). Only the LABEL comes from
    // `order.items`, the order's own scripts: `only.name` is the vendor's
    // placeholder ("Partell (RequestMyMeds)"), which is not what was
    // prescribed, so the charge screen read as paying the pharmacy's name
    // rather than the patient's medicine. Only reached when the loop above
    // found nothing, so no existing integration changes behaviour.
    if (prefillItems.length === 0 && order.amount > 0) {
      const only = axiom?.products.find((p) => p.quoteItemCostId != null);
      // Priced scripts (each line carries its amount and they add up to the
      // order's) become one payment line each — "Semaglutide … $175",
      // "Syringe $5" — rather than one line under a single name. Orders from
      // before per-script pricing have no line amounts and keep the one line.
      const priced = (order.items ?? []).filter((i) => (i.amount ?? 0) > 0);
      const pricedSum = priced.reduce((sum, i) => sum + (i.amount ?? 0), 0);
      if (only?.quoteItemCostId != null && priced.length > 0 && Math.abs(pricedSum - order.amount) < 0.01) {
        for (const i of priced) {
          prefillItems.push({
            quoteItemCostId: only.quoteItemCostId,
            quantity: 1,
            unitPrice: i.amount ?? 0,
            productTypeName: i.name,
          });
        }
      } else if (only?.quoteItemCostId != null) {
        const scripts = (order.items ?? []).map((i) => i.name).filter(Boolean);
        prefillItems.push({
          quoteItemCostId: only.quoteItemCostId,
          quantity: 1,
          unitPrice: order.amount,
          productTypeName: scripts.length > 0 ? scripts.join(', ') : only.name,
        });
      }
    }

    if (prefillItems.length === 0) {
      toast.error('Could not rebuild the charge for this order. Take the payment from Payments instead.');
      return;
    }
    setPaySource({ patientId, orderId: order.integrationOrderId, prefillItems });
  }

  /** Ask first (see the cancel dialog in `modals`); the dialog runs doCancel. */
  async function cancel(order: IntegrationOrder, patientId: number) {
    setPendingCancel({ order, patientId });
  }

  async function doCancel(order: IntegrationOrder, patientId: number) {
    try {
      const res = await cancelIntegrationOrder(patientId, order.integrationOrderId, integrationKey);
      refresh();
      toast.success(
        res.refundNeeded
          ? 'Order cancelled. A payment was already collected — issue a refund from Payments.'
          : 'Order cancelled.',
      );
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  }

  async function retry(order: IntegrationOrder, patientId: number) {
    if (!window.confirm('Re-send this order to the pharmacy? It is the same order and the same charge — nothing new is billed.')) return;
    try {
      const res = await retryIntegrationOrder(patientId, order.integrationOrderId);
      refresh();
      // The call succeeding only means the retry RAN. Report the pharmacy's
      // answer, or a second failure reads as a success and staff stop looking.
      if (res.accepted) {
        toast.success('Order re-sent — the pharmacy accepted it.');
      } else {
        toast.error(res.error ?? 'The pharmacy rejected the order again.');
      }
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  }

  function handlePaymentSaved(paymentId?: number) {
    const p = pay;
    setPay(null);
    if (!p) return;
    const siblings = p.siblingOrderIds ?? [];
    if (siblings.length > 0) {
      void releaseCombined(p, paymentId);
      return;
    }
    void (async () => {
      try {
        // Pass the payment we just took, so it is attached to this order and
        // stops being offered as available for the next one (SCRUM-1401).
        const res = await markIntegrationOrderPaid(p.patientId, p.orderId, {
          collectedPaymentID: paymentId,
        });
        toast.success(
          res.fulfilled
            ? `Payment collected — the order is now scheduled and sends to the pharmacy ${submitDelayPhrase(axiom?.submitDelayMinutes)}.`
            : 'Payment collected.',
        );
      } catch (err) {
        toast.error(getErrorMessage(err, 'Payment succeeded but releasing the order failed — use “Finish payment” on the order to retry.'));
      } finally {
        refresh();
      }
    })();
  }

  /**
   * One payment, several orders (a combined cart): mark EACH paid with the same
   * collected payment. Each is released on its own, so one failing never holds
   * the other back — and the toast names exactly which needs "Finish payment".
   */
  async function releaseCombined(p: PayState, paymentId?: number) {
    const ids = [p.orderId, ...(p.siblingOrderIds ?? [])];
    const released: number[] = [];
    let allFulfilled = true;
    const failed: Array<{ id: number; message: string }> = [];
    for (const id of ids) {
      try {
        const res = await markIntegrationOrderPaid(p.patientId, id, { collectedPaymentID: paymentId });
        released.push(id);
        if (!res.fulfilled) allFulfilled = false;
      } catch (err) {
        failed.push({ id, message: getErrorMessage(err, 'releasing it failed') });
      }
    }
    const list = (xs: number[]) => xs.map((id) => `#${id}`).join(' and ');
    if (released.length > 0) {
      toast.success(
        allFulfilled
          ? `Payment collected for order${released.length > 1 ? 's' : ''} ${list(released)} — now scheduled; sends to the pharmacy ${submitDelayPhrase(axiom?.submitDelayMinutes)}.`
          : `Payment collected for order${released.length > 1 ? 's' : ''} ${list(released)}.`,
      );
    }
    for (const f of failed) {
      toast.error(`Payment succeeded but order #${f.id} was not released (${f.message}) — use “Finish payment” on that order to retry.`);
    }
    refresh();
  }

  const cancelTarget = pendingCancel?.order ?? null;
  // Same test the server uses: an order has reached Axiom once it has Axiom's
  // order reference, and only then does cancelling go to the pharmacy.
  const cancelNotSent = cancelTarget != null && !cancelTarget.externalRef;
  const closeOrderForm = () => { setOrderOpen(false); setReorderSeed(null); setEditOrder(null); setCloneShipFrom(null); setResendFrom(null); };

  const modals = (
    <>
      <ConfirmDialog
        isOpen={pendingCancel !== null}
        onClose={() => setPendingCancel(null)}
        onConfirm={async () => {
          const p = pendingCancel;
          setPendingCancel(null);
          if (p) await doCancel(p.order, p.patientId);
        }}
        variant="danger"
        title={cancelTarget ? `Cancel order #${cancelTarget.integrationOrderId}?` : 'Cancel order?'}
        message={
          cancelNotSent ? (
            <>
              This order hasn’t reached {isRmm ? 'Partell' : 'Axiom'} yet, so it will simply be stopped here
              and any pending charge voided. It can’t be undone — use <b>Re-send</b>{' '}
              later to place it again as a new order.
            </>
          ) : isRmm ? (
            <>
              Partell has no way to cancel a prescription through its system: this marks
              it <b>cancelled in Prospera only</b> and voids any pending charge.{' '}
              <b>Call Partell</b> to stop the pharmacy filling it. A payment already
              collected must be refunded separately from Payments.
            </>
          ) : (
            <>
              This cancels the order <b>with Axiom</b>: the pharmacy is told to stop
              filling it. It can’t be undone — use <b>Re-send</b> later to place it
              again as a new order. A payment already collected must be refunded
              separately from Payments.
            </>
          )
        }
        confirmLabel="Yes, cancel order"
        cancelLabel="Keep order"
      />
      {axiom && isRmm && (
        <RmmPrescriptionModal
          isOpen={orderOpen}
          onClose={closeOrderForm}
          patientId={activePatientId}
          seedOrder={reorderSeed}
          shipFromOrder={cloneShipFrom}
          resentFromOrderId={resendFrom}
          editOrder={editOrder}
          onUpdated={() => { closeOrderForm(); refresh(); }}
          onSubmitted={(created) => {
            refresh();
            setReorderSeed(null);
            setResendFrom(null);
            // Charged to the patient: ask for the payment now, as the peptide
            // flow does (handleOrderedGroup) — whether or not the practice
            // holds orders for payment. The chooser also links a payment the
            // patient already made. A held order cannot reach the pharmacy
            // until it is paid; an unheld one would otherwise sit with a
            // pending charge and no prompt (SCRUM-2121).
            if (created?.billingMode === 'charge_patient' && (created.amount ?? 0) > 0) {
              setOrderOpen(false);
              finishPayment(
                { integrationOrderId: created.integrationOrderId, amount: created.amount, metadata: null, items: created.items ?? [] } as never,
                activePatientId,
              );
            }
          }}
        />
      )}
      {axiom && !isRmm && (
        <AxiomOrderModal
          patientId={activePatientId}
          integration={axiom}
          integrationKey={integrationKey}
          // Combined cart for NEW orders only; anything opened from an
          // existing order stays on that order's own (pens) module.
          vialsIntegration={combined && !editOrder && !reorderSeed && !cloneShipFrom ? vialsIntegration : null}
          onOrderedGroup={handleOrderedGroup}
          isOpen={orderOpen}
          editOrder={editOrder}
          shipFromOrder={cloneShipFrom}
          resentFromOrderId={resendFrom}
          seedOrder={reorderSeed}
          paidReference={paidReference}
          onClose={() => { setOrderOpen(false); setReorderSeed(null); setEditOrder(null); setCloneShipFrom(null); setResendFrom(null); }}
          onOrdered={handleOrdered}
          onUpdated={() => {
            // An in-place edit is finished when it is saved: no payment step
            // follows, because the order already has its charge (the amount is
            // re-synced server-side when items change).
            setOrderOpen(false);
            setEditOrder(null);
            refresh();
            toast.success('Order updated.');
          }}
        />
      )}
      {paySource !== null && (
        <PaymentSourceModal
          patientId={paySource.patientId}
          orderId={paySource.orderId}
          siblingOrderIds={paySource.siblingOrderIds}
          cartTotal={paySource.siblingOrderIds?.length
            ? Math.round(paySource.prefillItems.reduce((sum, l) => sum + (l.unitPrice ?? 0) * (l.quantity ?? 1), 0) * 100) / 100
            : undefined}
          onChargeNew={() => { setPay(paySource); setPaySource(null); }}
          onLinked={() => { setPaySource(null); refresh(); }}
          onClose={() => setPaySource(null)}
        />
      )}
      {pay !== null && (
        <ChargePaymentModal
          open
          patientId={pay.patientId}
          prefillItems={pay.prefillItems}
          onClose={() => setPay(null)}
          onSaved={handlePaymentSaved}
        />
      )}
    </>
  );

  return { axiom, combined, onboarded, onboardingKnown, startNewOrder, reorder, clone, resend, edit, finishPayment, cancel, retry, modals };
}
